import { isSubtaskHierarchyLevel } from '@atlassian/jira-issue-type-hierarchies/src/index.tsx';
import type { CardEntryGroup, CardId } from '../../../model/types.tsx';
import type { CardEntityState } from '../../../state/reducers/entities/card/types.tsx';
import type { State } from '../../../state/reducers/types.tsx';
import {
	getCardOrderByCardlistId,
	getCardOrderGlobal,
} from '../../../state/selectors/card-order/index.tsx';
import { getCards } from '../../../state/selectors/cards/index.tsx';

export const getIsMovingSubtasks = (cards: CardEntityState, cardsToMove: CardId[]) =>
	cardsToMove.some(
		(cardId) => cards[cardId] && isSubtaskHierarchyLevel(cards[cardId].typeHierarchyLevel),
	);

const removeSubtasks = (cards: CardEntityState, cardIds: CardId[]) =>
	cardIds.filter(
		(cardId) => cards[cardId] && !isSubtaskHierarchyLevel(cards[cardId].typeHierarchyLevel),
	);

export const getCMPCardsToMove = ({
	cardIds,
	getCardListIdByCardId,
	getCardGroupByParentId,
	isCmpScrumBacklog,
}: {
	cardIds: CardId[];
	getCardListIdByCardId: (id: number) => string | undefined;
	getCardGroupByParentId: (cardListId: string, parentId: number) => CardEntryGroup | undefined;
	isCmpScrumBacklog: boolean;
}) => {
	// For each card, we check if it is the parent of
	// a collapsed card group in kanban, or any card
	// group in scurm. If yes, we take the subtasks in
	// this card group and add them to the subtasksToMove.
	const subtasksToMove = cardIds.flatMap((cardId) => {
		const listId = getCardListIdByCardId(cardId);
		const cardGroup = listId && getCardGroupByParentId(listId, cardId);

		if (cardGroup && (isCmpScrumBacklog || cardGroup.isCollapsed)) {
			return cardGroup.cardIds;
		}

		return [];
	});

	return [...cardIds, ...subtasksToMove];
};

export const orderCardIds = (cardIdOrder: CardId[], cardIds: CardId[]) =>
	cardIdOrder.filter((cardId) => cardIds.includes(cardId));

export const getNewCardOrder = (
	state: State,
	cardListId: string,
	cardIds: CardId[],
	beforeCardId: CardId | null,
	afterCardId: CardId | null,
	isDroppedInTransitionZone: boolean,
) => {
	const globalCardOrder = getCardOrderGlobal(state);
	const cardListCardOrder = getCardOrderByCardlistId(state, cardListId);
	const allCards = getCards(state);

	// 1. Handle when cards are dropped and ranked within a cardlist.
	// This could include both subtasks and non-subtasks, where
	// the card that was dragged was a non-subtask.
	// This can only occur when the backlog is sorted by rank.
	if (!isDroppedInTransitionZone && (beforeCardId || afterCardId)) {
		const subtaskCardIds: CardId[] = [];
		const nonSubtaskCardIds: CardId[] = [];

		cardIds.forEach((cardId) => {
			const card = allCards[cardId];
			const isSubtask = isSubtaskHierarchyLevel(card.typeHierarchyLevel);
			if (isSubtask) {
				subtaskCardIds.push(cardId);
			} else {
				nonSubtaskCardIds.push(cardId);
			}
		});

		// a. Order subtasks within the cardlist according to their global rank
		const newCardOrder = orderCardIds(globalCardOrder, [...subtaskCardIds, ...cardListCardOrder]);

		const insertIndex = beforeCardId
			? newCardOrder.indexOf(beforeCardId)
			: newCardOrder.indexOf(afterCardId || 0) + 1;

		// b. Insert sorted non-subtasks into the position in the cardlist they were dropped
		newCardOrder.splice(insertIndex, 0, ...orderCardIds(globalCardOrder, nonSubtaskCardIds));

		return newCardOrder;
	}

	// 2. Handle when cards are dropped in a cardlist where the backlog is not
	// sorted by rank, or when the dragged card is a subtask, and therefore
	// not itself rankable.
	return orderCardIds(globalCardOrder, [...cardIds, ...cardListCardOrder]);
};

export const getNewCardOrderGlobal = (
	state: State,
	cardIds: CardId[],
	beforeCardId: CardId | null,
	afterCardId: CardId | null,
	isMovingSubtasks: boolean,
) => {
	const globalCardOrder = getCardOrderGlobal(state);
	const cards = getCards(state);

	if (!(beforeCardId || afterCardId)) {
		return globalCardOrder;
	}

	let cardsToMove = cardIds.concat();

	// When multiple cards including both subtasks and
	// non-subtasks have moved, and the dropped card is
	// a non-subtask, the subtasks will keep their global order,
	// while the non-subtasks will have their order changed
	// based on beforeCardId or afterCardId
	if (isMovingSubtasks) {
		cardsToMove = removeSubtasks(cards, cardIds);
	}

	// Remove all the cards that have moved, to be
	// placed back into their new position below
	const newCardOrderGlobal = globalCardOrder.filter((cardId) => !cardsToMove.includes(cardId));

	const insertIndex = beforeCardId
		? newCardOrderGlobal.indexOf(beforeCardId)
		: // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			newCardOrderGlobal.indexOf(afterCardId!) + 1;

	newCardOrderGlobal.splice(insertIndex, 0, ...orderCardIds(globalCardOrder, cardsToMove));

	return newCardOrderGlobal;
};
